<template>
  <div
    ref="root"
    role="group"
    :class="[
      $style.callout,
      computedLayout === 'multiline' && $style.callout_multiline,
      variant === 'experimental'
        ? [
            $style.callout_experimental,
            'bg-yellow-400/10 dark:bg-yellow-900/25',
          ]
        : 'bg-card',
    ]"
    class="shrink-0 py-2"
  >
    <div
      :class="$style.icon"
      class="my-1 grid h-5 w-4 shrink-0 place-items-center"
    >
      <InformationCircleIcon
        v-if="variant === 'info'"
        class="size-4 text-muted-foreground"
      />
      <CheckCircleIcon
        v-else-if="variant === 'success'"
        class="size-4 text-green-500"
      />
      <ExclamationTriangleIcon
        v-else-if="variant === 'warning'"
        class="size-4 text-yellow-600 dark:text-yellow-500"
      />
      <ExclamationCircleIcon
        v-else-if="variant === 'error'"
        class="size-4 text-red-400"
      />
      <ExclamationTriangleIcon
        v-else-if="variant === 'experimental'"
        class="size-4 text-yellow-600 dark:text-yellow-500"
      />
    </div>

    <div :class="$style.body" class="my-1 text-sm">
      <span v-if="slots.title" class="font-bold">
        <slot name="title"></slot>
      </span>

      <slot />
    </div>

    <div
      v-if="slots.actions"
      :class="$style.actions"
      class="flex h-7 items-center gap-2.5"
    >
      <slot name="actions"></slot>
    </div>

    <div v-if="onClose" :class="$style.close" class="my-1">
      <button
        type="button"
        aria-label="Dismiss message"
        class="grid size-5 place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground active:bg-muted active:text-foreground"
        @click="onClose"
      >
        <XMarkIcon class="size-4" />
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onWatcherCleanup, ref, useTemplateRef, watch } from "vue";

import CheckCircleIcon from "@/icons/heroicons/check-circle-micro.svg";
import ExclamationCircleIcon from "@/icons/heroicons/exclamation-circle-micro.svg";
import ExclamationTriangleIcon from "@/icons/heroicons/exclamation-triangle-micro.svg";
import InformationCircleIcon from "@/icons/heroicons/information-circle-micro.svg";
import XMarkIcon from "@/icons/heroicons/x-mark-micro.svg";

const props = withDefaults(
  defineProps<{
    variant?: "error" | "experimental" | "info" | "success" | "warning";
    layout?: "auto" | "multiline" | "singleline";
    onClose?: () => void;
  }>(),
  {
    variant: "info",
    layout: "auto",
  }
);

const slots = defineSlots<{
  /** Callout message */
  default(props: {}): unknown;
  /** Short descriptive title */
  title?(props: {}): unknown;
  /** Available actions */
  actions?(props: {}): unknown;
}>();

const root = useTemplateRef("root");
const reflowing = ref(false);

/** Wait this long after the last resize before measuring — avoids zigzag during drawer width animations. */
const LAYOUT_SETTLE_MS = 150;

watch([root, () => props.layout], ([el, layout]) => {
  if (!el || layout !== "auto") {
    return;
  }

  let singlelineWidth = 0;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  let latestEntry: ResizeObserverEntry | null = null;

  const inlineSizeFromEntry = (entry: ResizeObserverEntry): number => {
    const box = entry.borderBoxSize[0];
    if (box) {
      return box.inlineSize;
    }

    return entry.contentRect.width;
  };

  const applyAutoLayout = () => {
    const entry = latestEntry;
    if (!entry) {
      return;
    }

    const target = entry.target;
    const inlineSize = inlineSizeFromEntry(entry);

    if (reflowing.value) {
      if (singlelineWidth > 0 && inlineSize >= singlelineWidth) {
        reflowing.value = false;
      }
    } else {
      singlelineWidth = target.scrollWidth;
      if (singlelineWidth > inlineSize) {
        reflowing.value = true;
      }
    }
  };

  const observer = new ResizeObserver((entries) => {
    latestEntry = entries[0] ?? null;
    if (settleTimer !== null) {
      clearTimeout(settleTimer);
    }
    settleTimer = setTimeout(() => {
      settleTimer = null;
      applyAutoLayout();
    }, LAYOUT_SETTLE_MS);
  });

  observer.observe(el, { box: "border-box" });
  onWatcherCleanup(() => {
    if (settleTimer !== null) {
      clearTimeout(settleTimer);
    }
    observer.disconnect();
  });
});

const computedLayout = computed(() => {
  if (props.layout === "auto") {
    return reflowing.value ? "multiline" : "singleline";
  }

  return props.layout;
});
</script>

<style module>
.callout {
  display: grid;
  /* minmax(0, 1fr) so the message column can shrink in narrow containers (e.g. drawers);
     otherwise min-width:auto + nowrap forces overflow and layout=auto always picks multiline. */
  grid-template-columns: auto minmax(0, 1fr);
  grid-auto-columns: auto;
  grid-auto-flow: column;
  align-items: start;
  row-gap: 0.25rem;
  padding-left: 1rem;

  .icon {
    margin-right: 0.5rem;
  }

  .body {
    min-width: 0;
    padding-right: 1rem;
  }

  .actions {
    flex-shrink: 0;
    padding-right: 1rem;
    white-space: nowrap;
  }

  .close {
    padding-right: 1rem;
  }

  .actions:has(+ .close) {
    padding-right: 0.5rem;
  }
}

.callout_experimental {
  background-image: repeating-linear-gradient(
    -45deg,
    rgba(0, 0, 0, 0.05),
    rgba(0, 0, 0, 0.05) 20px,
    transparent 20px,
    transparent 40px
  );

  :global(.dark) & {
    background-image: repeating-linear-gradient(
      -45deg,
      rgba(255, 255, 255, 0.025),
      rgba(255, 255, 255, 0.025) 20px,
      transparent 20px,
      transparent 40px
    );
  }
}

.callout_multiline {
  grid-auto-flow: row;
  grid-template-columns: auto minmax(0, 1fr) auto;

  .icon {
    grid-row: 1;
    grid-column: 1;
  }

  .body {
    grid-row: 1;
    grid-column: 2;
  }

  .close {
    grid-row: 1;
    grid-column: 3;
  }

  .actions {
    grid-row: 2;
    grid-column: 2 / -1;
    justify-content: end;
  }
}
</style>
